/**
 * Scan-to-Cart – Barcode Scanner für Kasa Beauty B2B
 *
 * Barcode → Variant-Mapping:
 *  - Seite 1 (50 Produkte) inline von Liquid als JSON
 *  - Restliche Seiten per Section Rendering API im Hintergrund
 *  - Entries kommen aus snippets/barcode-map-entries.liquid: ein Eintrag
 *    pro Barcode, egal ob Einzelwert, Liste im Barcode-Feld (, ; | /)
 *    oder zukünftiges Barcode-Array (variant.barcodes)
 *  - indexBarcodeMap() legt für jeden Schlüssel Alias-Schlüssel an
 *    (ohne Sonderzeichen, ohne führende Nullen, EAN-13/GTIN-14
 *    aufgefüllt), damit jede Schreibweise eines Scans trifft
 *
 * Scan-Strategie (Fallback-Kette):
 *  1. ZXing Browser lib (Shopify asset) – 0° im Hauptloop, dazu gedrehte
 *     Einzelbilder (startRotationSupplement), damit Barcodes in jedem
 *     Winkel erkannt werden
 *  2. Native BarcodeDetector API (Chrome/Edge Android) – drehungsunabhängig
 *  3. Manuelle EAN-Eingabe
 */

(function () {
  'use strict';

  /* ============================================================
     Constants
     ============================================================ */
  var DEBOUNCE_MS       = 2500;   // ignore same barcode within this window
  var AUTO_RESUME_MS    = 2000;   // auto-resume scanning after showing result
  var ADDED_DISPLAY_MS  = 1800;   // how long "added to cart" message shows
  var PAGE_CONCURRENCY  = 4;      // parallel AJAX page loads
  var LOAD_WAIT_MAX_MS  = 30000;  // max wait for background page loading
  var ROTATION_ANGLES   = [90, 45, 135, 30, 60, 120, 150, 15, 75, 105, 165]; // extra decode angles (0° = main loop)
  var ROTATION_STEP_MS  = 80;     // pause between two rotated decode attempts
  var ROTATION_MAX_SIDE = 1280;   // longest side of the rotated frame (downscaled above)
  var ROTATION_CONFIRM_MS = 1500; // rotated hit counts only if read twice within this window

  /* ============================================================
     State
     ============================================================ */
  var codeReader  = null;
  var scanning    = false;
  var _ZXingRef   = null;            // cached ZXing reference
  var scannedItems = [];
  var _lastScannedCode = '';
  var _lastScanTime    = 0;
  var _autoResumeTimer = 0;
  var _processingBarcode = false;    // guard: prevent concurrent processBarcode calls

  // Camera switching
  var _cameras        = [];   // list of available video input devices
  var _cameraIndex    = 0;    // currently active camera index
  var _activeStream   = null; // currently active MediaStream

  /* ============================================================
     DOM cache
     ============================================================ */
  var ui = {};

  function cacheDom() {
    ui.video          = document.getElementById('scanner-preview');
    ui.status         = document.getElementById('scanner-status');
    ui.result         = document.getElementById('scan-result');
    ui.productImage   = document.getElementById('scan-product-image');
    ui.productVendor  = document.getElementById('scan-product-vendor');
    ui.productTitle   = document.getElementById('scan-product-title');
    ui.productVariant = document.getElementById('scan-product-variant');
    ui.productPrice   = document.getElementById('scan-product-price');
    ui.productSku     = document.getElementById('scan-product-sku');
    ui.productStock   = document.getElementById('scan-product-stock');
    ui.productLink    = document.getElementById('scan-product-link');
    ui.addBtn         = document.getElementById('scan-add-to-cart');
    ui.qtyInput       = document.getElementById('scan-quantity');
    ui.qtyMinus       = document.getElementById('scan-qty-minus');
    ui.qtyPlus        = document.getElementById('scan-qty-plus');
    ui.addedMsg       = document.getElementById('scan-added-msg');
    ui.cartList       = document.getElementById('scan-cart-list');
    ui.cartListItems  = document.getElementById('scan-cart-list-items');
    ui.cartListCount  = document.getElementById('scan-cart-list-count');
    ui.manualToggle   = document.getElementById('scan-manual-toggle');
    ui.manualForm     = document.getElementById('scan-manual-form');
    ui.manualInput    = document.getElementById('scan-manual-input');
    ui.manualSubmit   = document.getElementById('scan-manual-submit');
    ui.overlayGuide   = document.querySelector('.scanner-overlay__guide');
    ui.permissionUI   = document.getElementById('scanner-permission');
    ui.permissionBtn  = document.getElementById('scanner-permission-btn');
    ui.switchCameraBtn = document.getElementById('scanner-switch-camera');
  }

  /* ============================================================
     Barcode Map
     ============================================================ */
  var _barcodeMapCache     = null;
  var _barcodeMapFullyLoaded = false;
  var _loadingPages        = false;
  var _pagesLoaded         = 1;

  function getBarcodeMap() {
    if (_barcodeMapCache) return _barcodeMapCache;
    var el = document.getElementById('barcode-map-data');
    if (!el) return {};
    try {
      _barcodeMapCache = indexBarcodeMap(JSON.parse(el.textContent));
    } catch (e) {
      _barcodeMapCache = {};
    }
    return _barcodeMapCache;
  }

  /* ---- Normalisierung & Alias-Index ----
     Ein physischer Barcode kann in mehreren Schreibweisen hinterlegt sein
     (UPC-A "858511001142" vs. EAN-13 "0858511001142", Sonderzeichen,
     Leerzeichen). Beim Indexieren registrieren wir für jeden Schlüssel
     zusätzlich alle Normalformen als Alias, ohne bestehende Schlüssel zu
     überschreiben. So trifft ein Scan unabhängig von der gespeicherten
     Schreibweise – egal ob der Barcode aus dem Einzelfeld, einer
     Komma-Liste oder dem Barcode-Array stammt. */
  function barcodeVariants(code) {
    var out = [];
    if (!code) return out;
    var cleaned = String(code).trim();
    if (!cleaned) return out;
    var alnum = cleaned.replace(/[^0-9A-Za-z]/g, '');
    var forms = [cleaned, alnum];
    if (/^\d+$/.test(alnum)) {
      forms.push(alnum.replace(/^0+/, ''));
      if (alnum.length < 13) forms.push(alnum.padStart(13, '0'));
      if (alnum.length < 14) forms.push(alnum.padStart(14, '0'));
    }
    for (var i = 0; i < forms.length; i++) {
      if (forms[i] && out.indexOf(forms[i]) === -1) out.push(forms[i]);
    }
    return out;
  }

  function indexBarcodeMap(map) {
    if (!map || typeof map !== 'object') return {};
    var keys = Object.keys(map);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (key.indexOf('__') === 0) { delete map[key]; continue; }
      var entry = map[key];
      if (!entry || typeof entry !== 'object') { delete map[key]; continue; }
      var forms = barcodeVariants(key);
      for (var f = 0; f < forms.length; f++) {
        if (!(forms[f] in map)) map[forms[f]] = entry;
      }
    }
    return map;
  }

  /* ---- Background page loading via Section Rendering API ---- */
  async function loadRemainingBarcodes() {
    if (_loadingPages || _barcodeMapFullyLoaded) return;
    _loadingPages = true;

    var metaEl = document.getElementById('barcode-collection-handle');
    if (!metaEl) { _barcodeMapFullyLoaded = true; _loadingPages = false; return; }

    var collectionHandle = metaEl.value || 'all';
    var totalPages    = parseInt(metaEl.dataset.totalPages, 10) || 1;
    var productsCount = parseInt(metaEl.dataset.productsCount, 10) || 0;
    var map = getBarcodeMap();
    var initialCount = Object.keys(map).length;

    if (totalPages <= 1) {
      _barcodeMapFullyLoaded = true;
      _loadingPages = false;
      return;
    }

    var queue = [];
    for (var p = 2; p <= totalPages; p++) queue.push(p);

    var active = 0;
    var totalNew = 0;

    await new Promise(function (resolve) {
      function next() {
        if (queue.length === 0 && active === 0) { resolve(); return; }
        while (active < PAGE_CONCURRENCY && queue.length > 0) {
          var page = queue.shift();
          active++;
          loadBarcodePage(collectionHandle, page, map)
            .then(function (count) { totalNew += count; active--; _pagesLoaded++; next(); })
            .catch(function ()     { active--; _pagesLoaded++; next(); });
        }
      }
      next();
    });

    _barcodeMapCache = map;
    _barcodeMapFullyLoaded = true;
    _loadingPages = false;
  }

  async function loadBarcodePage(collectionHandle, page, map) {
    var url = '/collections/' + collectionHandle + '?page=' + page + '&sections=barcode-map-ajax';
    var res = await fetch(url);
    if (!res.ok) throw new Error('HTTP ' + res.status);

    var data = await res.json();
    var html = data['barcode-map-ajax'] || '';
    var match = html.match(/<script[^>]*id="barcode-map-data"[^>]*>([\s\S]*?)<\/script>/);
    if (!match || !match[1]) return 0;

    var pageMap;
    try { pageMap = JSON.parse(match[1]); } catch (e) { return 0; }

    pageMap = indexBarcodeMap(pageMap);

    var newCount = 0;
    var keys = Object.keys(pageMap);
    for (var i = 0; i < keys.length; i++) {
      if (!map[keys[i]]) newCount++;
      map[keys[i]] = pageMap[keys[i]];
    }
    return newCount;
  }

  /* ============================================================
     Barcode matching helper
     fieldValue: String (ein Barcode oder Liste mit , ; | / getrennt)
                 ODER Array von Barcodes (zukünftiges Array-Feld).
     Vergleicht alle Normalformen (führende Nullen, EAN-13/14-Auffüllung,
     Sonderzeichen) beider Seiten.
     ============================================================ */
  function splitBarcodeField(fieldValue) {
    var list = [];
    if (!fieldValue) return list;
    var raw = Array.isArray(fieldValue) ? fieldValue : String(fieldValue).split(/[,;|\/]+/);
    for (var i = 0; i < raw.length; i++) {
      var c = raw[i] == null ? '' : String(raw[i]).trim();
      if (c && list.indexOf(c) === -1) list.push(c);
    }
    return list;
  }

  function barcodeMatches(fieldValue, scanned) {
    if (!fieldValue || !scanned) return false;
    var scannedForms = barcodeVariants(scanned);
    var candidates = splitBarcodeField(fieldValue);
    for (var i = 0; i < candidates.length; i++) {
      var forms = barcodeVariants(candidates[i]);
      for (var f = 0; f < forms.length; f++) {
        if (scannedForms.indexOf(forms[f]) !== -1) return true;
      }
    }
    return false;
  }

  /* ============================================================
     Lookup
     ============================================================ */
  function lookupBarcode(ean) {
    var map = getBarcodeMap();
    // Alle Normalformen des Scans gegen den Alias-Index prüfen
    // (Direkttreffer, Sonderzeichen entfernt, ohne führende Nullen,
    //  auf EAN-13 / GTIN-14 aufgefüllt).
    var forms = barcodeVariants(ean);
    for (var i = 0; i < forms.length; i++) {
      if (Object.prototype.hasOwnProperty.call(map, forms[i])) return map[forms[i]];
    }
    return null;
  }

  /* ============================================================
     Process barcode (scanned or typed)
     ============================================================ */
  async function processBarcode(ean, fromCamera) {
    if (fromCamera) {
      var now = Date.now();
      if (ean === _lastScannedCode && (now - _lastScanTime) < DEBOUNCE_MS) return;
      _lastScannedCode = ean;
      _lastScanTime = now;
    }

    // Guard: prevent multiple concurrent lookups (e.g. when camera fires many
    // frames of the same barcode while background loading is still in progress).
    if (_processingBarcode) return;
    _processingBarcode = true;

    try {
    var variant = lookupBarcode(ean);
    if (variant) {
      showProduct(variant);
      return;
    }

    // Wait for background loading if still in progress.
    // Poll with a short interval so we don't block the thread longer than needed.
    if (!_barcodeMapFullyLoaded) {
      setStatus('Lade Barcode-Daten… Bitte warten.', 'scanning');
      var waitStart = Date.now();
      while (!_barcodeMapFullyLoaded && (Date.now() - waitStart) < LOAD_WAIT_MAX_MS) {
        await delay(200);
      }
      variant = lookupBarcode(ean);
      if (variant) { showProduct(variant); return; }
    }

    // Fallback: live search
    setStatus('Suche Barcode „' + ean + '"…', 'scanning');
    variant = await liveSearchBarcode(ean);
    if (variant) {
      var map = getBarcodeMap();
      var codes = (variant.barcodes && variant.barcodes.length) ? variant.barcodes : [ean];
      var extra = {};
      for (var ci = 0; ci < codes.length; ci++) extra[codes[ci]] = variant;
      extra[ean] = variant;
      indexBarcodeMap(extra);
      var extraKeys = Object.keys(extra);
      for (var ki = 0; ki < extraKeys.length; ki++) {
        if (!map[extraKeys[ki]]) map[extraKeys[ki]] = extra[extraKeys[ki]];
      }
      _barcodeMapCache = map;
      showProduct(variant);
    } else {
      showNotFound(ean);
    }
    } finally {
      _processingBarcode = false;
    }
  }

  /* ============================================================
     Live barcode search (Shopify Search + Product API fallback)
     ============================================================ */
  /* Hintergrund: Shopify indexiert für die Storefront-Suche ALLE Barcodes
     einer Variante – auch die Einträge des neuen Barcode-Arrays im Admin.
     products/<handle>.js liefert aber nur den ersten Barcode. Deshalb:
     1. Suche nach dem Scan → Kandidaten-Produkte
     2. Variante per Barcode-Vergleich bestätigen (Einzelfeld / Komma-Liste)
     3. Kein Treffer, aber Produkt mit genau EINER Variante gefunden → diese
        Variante akzeptieren (der Barcode steckt dann nur im Array). Bei
        mehreren Varianten ist der Scan nicht eindeutig → kein Treffer. */
  function variantFromProductJson(pData, v, allCodes, scanned) {
    var imgSrc = '';
    if (v.featured_image && v.featured_image.src) imgSrc = v.featured_image.src;
    else if (pData.featured_image) imgSrc = pData.featured_image;
    var codes = allCodes.slice();
    if (scanned && codes.indexOf(scanned) === -1) codes.push(scanned);
    return {
      variant_id: v.id,
      product_title: pData.title,
      variant_title: v.title,
      price: v.price,
      compare_at_price: v.compare_at_price || 0,
      sku: v.sku || '',
      barcode: v.barcode,
      barcodes: codes,
      available: v.available,
      vendor: pData.vendor || '',
      url: '/products/' + pData.handle,
      image: imgSrc,
      inventory_quantity: null,
      inventory_policy: v.inventory_policy || 'deny',
      inventory_management: v.inventory_management || null
    };
  }

  async function liveSearchBarcode(ean) {
    var singleVariantFallback = null;
    try {
      var searchUrl = '/search/suggest.json?q=' + encodeURIComponent(ean) + '&resources[type]=product&resources[limit]=5';
      var res = await fetch(searchUrl);
      if (!res.ok) return null;

      var data = await res.json();
      var products = (data.resources && data.resources.results && data.resources.results.products) || [];

      for (var i = 0; i < products.length; i++) {
        try {
          var pRes = await fetch('/products/' + products[i].handle + '.js');
          if (!pRes.ok) continue;
          var pData = await pRes.json();

          for (var vi = 0; vi < pData.variants.length; vi++) {
            var v = pData.variants[vi];
            var allCodes = splitBarcodeField(v.barcode).concat(splitBarcodeField(v.barcodes));
            if (barcodeMatches(allCodes, ean)) {
              return variantFromProductJson(pData, v, allCodes, null);
            }
          }

          // Barcode nur im Admin-Array hinterlegt (nicht in products.js sichtbar):
          // eindeutig, wenn das gefundene Produkt genau eine Variante hat.
          if (!singleVariantFallback && pData.variants.length === 1 && /^\d{8,14}$/.test(String(ean).trim())) {
            var only = pData.variants[0];
            singleVariantFallback = variantFromProductJson(
              pData, only, splitBarcodeField(only.barcode).concat(splitBarcodeField(only.barcodes)), String(ean).trim()
            );
          }
        } catch (e) { /* skip product */ }
      }
    } catch (err) { /* network error */ }
    return singleVariantFallback;
  }

  /* ============================================================
     UI helpers
     ============================================================ */
  function setStatus(text, type) {
    ui.status.textContent = text;
    ui.status.className = 'scanner-status scanner-status--' + type;
  }

  function formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    return (cents / 100).toFixed(2).replace('.', ',') + '\u00A0€';
  }

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  function delay(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  var PLACEHOLDER_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%23f5f5f5'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' font-size='14' fill='%23999'%3EKein Bild%3C/text%3E%3C/svg%3E";

  /* ============================================================
     Show product result card
     ============================================================ */
  function showProduct(v) {
    cancelAutoResume();

    // Image
    var imgSrc = v.image || '';
    if (imgSrc) {
      if (imgSrc.indexOf('width=') === -1 && imgSrc.indexOf('cdn.shopify.com') !== -1) {
        imgSrc += (imgSrc.indexOf('?') === -1 ? '?' : '&') + 'width=400';
      }
      if (imgSrc.indexOf('//') === 0) imgSrc = 'https:' + imgSrc;
    }
    ui.productImage.style.display = '';
    ui.productImage.alt = v.product_title || '';
    ui.productImage.onerror = function () { this.src = PLACEHOLDER_SVG; this.onerror = null; };
    ui.productImage.src = imgSrc || PLACEHOLDER_SVG;

    // Text fields
    ui.productVendor.textContent  = v.vendor || '';
    ui.productTitle.textContent   = v.product_title || '';
    if (ui.productLink) ui.productLink.href = v.url || '#';
    ui.productVariant.textContent = (v.variant_title && v.variant_title !== 'Default Title') ? v.variant_title : '';
    ui.productPrice.textContent   = formatMoney(v.price);
    ui.productSku.textContent     = v.sku ? 'SKU: ' + v.sku : '';

    // Stock / preorder / unavailable logic
    var isPreorder = v.available
      && v.inventory_policy === 'continue'
      && typeof v.inventory_quantity === 'number'
      && v.inventory_quantity < 0;

    if (isPreorder) {
      ui.productStock.textContent = '⚠ Vorbestellung';
      ui.productStock.className   = 'scan-result__stock scan-result__stock--preorder';
      ui.addBtn.disabled = false;
    } else if (v.available) {
      ui.productStock.textContent = '✓ Auf Lager';
      ui.productStock.className   = 'scan-result__stock scan-result__stock--in-stock';
      ui.addBtn.disabled = false;
    } else {
      ui.productStock.textContent = '✗ Nicht verfügbar';
      ui.productStock.className   = 'scan-result__stock scan-result__stock--out-of-stock';
      ui.addBtn.disabled = true;
    }

    ui.qtyInput.value = 1;
    ui.addedMsg.classList.remove('visible');
    ui.result.hidden = false;
    ui.result.dataset.variantId = v.variant_id;

    setStatus('Produkt gefunden: ' + (v.product_title || ''), 'found');

    // Flash overlay guide green
    if (ui.overlayGuide) {
      ui.overlayGuide.classList.add('scanner-overlay__guide--success');
      requestAnimationFrame(function () {
        setTimeout(function () {
          ui.overlayGuide.classList.remove('scanner-overlay__guide--success');
        }, 1200);
      });
    }
  }

  function showNotFound(ean) {
    ui.result.hidden = true;
    setStatus('Kein Produkt für EAN „' + ean + '" gefunden.', 'not-found');
  }

  /* ============================================================
     Auto-resume scanning
     After add-to-cart, auto-dismiss result and keep scanning.
     ============================================================ */
  function scheduleAutoResume() {
    cancelAutoResume();
    _autoResumeTimer = setTimeout(function () {
      _autoResumeTimer = 0;
      ui.result.hidden = true;
      ui.addedMsg.classList.remove('visible');
      if (!scanning) startScanning();
      setStatus('Barcode vor die Kamera halten…', 'scanning');
    }, AUTO_RESUME_MS);
  }

  function cancelAutoResume() {
    if (_autoResumeTimer) {
      clearTimeout(_autoResumeTimer);
      _autoResumeTimer = 0;
    }
  }

  /* ============================================================
     Add to Cart (Shopify AJAX API)
     ============================================================ */
  async function addToCart() {
    var variantId = ui.result.dataset.variantId;
    var qty = parseInt(ui.qtyInput.value, 10) || 1;
    if (!variantId) return;

    ui.addBtn.classList.add('loading');
    ui.addBtn.disabled = true;

    try {
      var res = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty }),
      });
      var data = await res.json();

      if (data.status && data.status === 422) {
        setStatus('Fehler: ' + (data.description || 'Konnte nicht hinzugefügt werden.'), 'error');
        ui.addBtn.classList.remove('loading');
        ui.addBtn.disabled = false;
        return;
      }

      // Success
      ui.addedMsg.classList.add('visible');
      setStatus('✓ ' + qty + '× hinzugefügt!', 'found');

      scannedItems.push({ title: ui.productTitle.textContent, quantity: qty, variantId: variantId });
      updateCartList();

      // Notify theme PubSub
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'scan-to-cart',
          productVariantId: variantId,
          cartData: data,
        });
      }

      refreshCartBubble();

      // Auto-resume scanning after brief confirmation
      scheduleAutoResume();

    } catch (err) {
      setStatus('Netzwerkfehler – bitte erneut versuchen.', 'error');
    } finally {
      ui.addBtn.classList.remove('loading');
      ui.addBtn.disabled = false;
    }
  }

  async function refreshCartBubble() {
    try {
      var res = await fetch(window.routes.cart_url + '.js');
      var cart = await res.json();
      document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(function (el) {
        el.textContent = cart.item_count;
      });
      document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
        if (cart.item_count > 0) el.classList.remove('hidden');
      });
    } catch (e) { /* silent */ }
  }

  /* ============================================================
     Session scanned-items list
     ============================================================ */
  function updateCartList() {
    if (!scannedItems.length) { ui.cartList.hidden = true; return; }
    ui.cartList.hidden = false;
    ui.cartListCount.textContent = scannedItems.length;
    ui.cartListItems.innerHTML = scannedItems.map(function (item) {
      return '<li class="scan-cart-list__item">'
        + '<span class="scan-cart-list__item-name">' + esc(item.title) + '</span>'
        + '<span class="scan-cart-list__item-qty">' + item.quantity + '×</span>'
        + '</li>';
    }).join('');
  }

  /* ============================================================
     Quantity +/–
     ============================================================ */
  function bindQuantity() {
    ui.qtyMinus.addEventListener('click', function () {
      var v = parseInt(ui.qtyInput.value, 10) || 1;
      if (v > 1) ui.qtyInput.value = v - 1;
    });
    ui.qtyPlus.addEventListener('click', function () {
      ui.qtyInput.value = (parseInt(ui.qtyInput.value, 10) || 1) + 1;
    });
  }

  /* ============================================================
     Manual EAN input
     ============================================================ */
  function bindManualInput() {
    ui.manualToggle.addEventListener('click', function () {
      ui.manualForm.classList.toggle('active');
      if (ui.manualForm.classList.contains('active')) ui.manualInput.focus();
    });
    ui.manualSubmit.addEventListener('click', function () {
      var ean = ui.manualInput.value.trim();
      if (ean) { processBarcode(ean, false); ui.manualInput.value = ''; }
    });
    ui.manualInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); ui.manualSubmit.click(); }
    });
  }

  /* ============================================================
     Camera enumeration & switching
     Research findings (2024/2025 Android Chrome):
     - Chrome on Android with multi-lens back cameras (Pixel 8, Samsung S10 etc.)
       exposes ALL lenses as separate videoinput devices via enumerateDevices().
     - Labels follow the pattern "camera2 N, facing back" — distinguishable by
       the number N (lower = primary/standard lens).
     - getUserMedia with deviceId: { exact: … } DOES work reliably on modern
       Chrome to open a specific lens, provided the deviceId is stable.
     - Front-cameras have labels containing "front" or "user" (facingMode).
     - The switch button should be shown whenever ≥2 back-facing cameras exist.
     ============================================================ */
  /* ============================================================
     Mobile-visible debug log
     Shows messages directly on screen — replaces console.log for
     devices where DevTools are not available.
     Call dbg('msg') anywhere; the overlay auto-hides after 8 s.
     Remove / comment out the dbg() calls once issues are resolved.
     ============================================================ */
  // var _dbgEl = null;
  // var _dbgTimer = 0;
  // function dbg(msg) {
  //   if (!_dbgEl) {
  //     _dbgEl = document.createElement('div');
  //     _dbgEl.style.cssText = [
  //       'position:fixed', 'bottom:0', 'left:0', 'right:0', 'z-index:99999',
  //       'background:rgba(0,0,0,.85)', 'color:#0f0', 'font:12px/1.4 monospace',
  //       'padding:6px 8px', 'max-height:40vh', 'overflow-y:auto',
  //       'pointer-events:none', 'white-space:pre-wrap', 'word-break:break-all'
  //     ].join(';');
  //     document.body.appendChild(_dbgEl);
  //   }
  //   var ts = new Date().toISOString().slice(11, 23);
  //   _dbgEl.textContent = '[' + ts + '] ' + msg + '\n' + _dbgEl.textContent;
  //   clearTimeout(_dbgTimer);
  //   _dbgTimer = setTimeout(function () {
  //     if (_dbgEl) { _dbgEl.textContent = ''; }
  //   }, 8000);
  // }
  function dbg() {} // debug output disabled for production

  /* ============================================================
     enumerateCameras()
     Research-backed implementation notes (2024/2025):

     PROBLEM 1 – Privacy gating (W3C spec §9.2.1):
       Before the first getUserMedia() grant, the browser returns at most ONE
       camera device with empty deviceId/label/groupId. Only AFTER a successful
       grant does enumerateDevices() expose all cameras with real IDs and labels.
       → Never rely on pre-grant enumeration to determine camera count.
       → Always call enumerateCameras() again AFTER getUserMedia() resolves.

     PROBLEM 2 – "default" deviceId (Android Chrome):
       Some browsers return deviceId = "default" for the primary camera before or
       during the grant phase. Passing { deviceId: { exact: "default" } } to
       getUserMedia() throws an OverconstrainedError. We filter these out.

     PROBLEM 3 – iOS Safari multi-lens (iOS < 17):
       iOS exposes only a single virtual "back camera" device regardless of how
       many physical lenses exist. The OS chooses the lens automatically.
       → On iOS, _cameras.length will remain 1 → switch button stays hidden.
       This is the correct behaviour; there is no WebAPI to force a specific lens.

     PROBLEM 4 – iOS Safari multi-lens (iOS 17+):
       Multiple cameras are now exposed, but deviceIds are session-scoped (reset
       on every page load). The current code uses deviceId only within a session,
       which is fine.

     PROBLEM 5 – Android Chrome: facingMode absent in enumerateDevices():
       The facingMode is NOT part of the MediaDeviceInfo object returned by
       enumerateDevices(). It is only available via track.getCapabilities() after
       opening the stream. We therefore rely on label-keyword filtering, which
       works on Android Chrome ("camera2 N, facing front/back") and most OEMs.
       Languages other than English: label may be in the device's UI language,
       so we only EXCLUDE cameras whose label explicitly contains a front keyword.
       If no labels are available, we keep all cameras (no false exclusions).

     PROBLEM 6 – Race condition between pre-grant and post-grant calls:
       The first enumerateCameras() call (before getUserMedia) may set _cameras
       to a 1-element list. The second call (after grant) overwrites it correctly.
       We do NOT update the switch button visibility in the pre-grant call to
       avoid a flash of "button visible" followed by "button hidden".
  ============================================================ */
  async function enumerateCameras(postGrant) {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return;
    }
    try {
      var devices = await navigator.mediaDevices.enumerateDevices();
      var videoDevices = devices.filter(function (d) { return d.kind === 'videoinput'; });

      // Filter out placeholder entries that have no real deviceId or only "default".
      // These appear before the permission grant and cannot be used with
      // { deviceId: { exact: ... } } — passing them causes OverconstrainedError.
      var hasRealIds = videoDevices.some(function (d) {
        return d.deviceId && d.deviceId !== 'default';
      });
      if (!hasRealIds) {
        // Pre-grant state: browser hides real IDs. Nothing useful to do yet.
        return;
      }

      var FRONT_KEYWORDS = ['front', 'user', 'facetime', 'selfie', 'vorder'];
      var hasLabels = videoDevices.some(function (d) { return !!d.label; });

      var backCameras;
      if (hasLabels) {
        // Only exclude cameras whose label explicitly identifies them as front-facing.
        // This preserves cameras with no label or ambiguous labels (better safe than
        // hiding a valid back camera).
        backCameras = videoDevices.filter(function (d) {
          var label = (d.label || '').toLowerCase();
          for (var i = 0; i < FRONT_KEYWORDS.length; i++) {
            if (label.indexOf(FRONT_KEYWORDS[i]) !== -1) return false;
          }
          return true;
        });
      } else {
        // No labels yet (pre-grant or device with privacy labels off).
        // Keep all video devices — we cannot distinguish front from back.
        backCameras = videoDevices;
      }

      var newList = backCameras.length > 0 ? backCameras : videoDevices;

      // Preserve the active camera index across re-enumerations by matching deviceId.
      if (_cameras.length > 0 && _cameraIndex < _cameras.length) {
        var prevId = _cameras[_cameraIndex].deviceId;
        var found = false;
        for (var j = 0; j < newList.length; j++) {
          if (newList[j].deviceId === prevId) { _cameraIndex = j; found = true; break; }
        }
        if (!found) _cameraIndex = 0;
      } else {
        _cameraIndex = 0;
      }

      _cameras = newList;

      // Only update the switch-button visibility AFTER the permission grant.
      // Before the grant, the browser exposes at most 1 camera (W3C spec §9.2.1),
      // so we would always hide the button — which would then flicker once the
      // post-grant enumeration reveals the real count.
      if (postGrant && ui.switchCameraBtn) {
        ui.switchCameraBtn.hidden = _cameras.length < 2;
      }
    } catch (e) {
      // Silent — enumeration failure is non-critical
    }
  }

  function stopActiveStream() {
    // Stop ZXing's decode loop WITHOUT calling reset() — reset() would invoke
    // stopStreams() internally, double-stopping our externally managed MediaStream
    // and clearing srcObject prematurely (causes black screen / incognito bug).
    if (codeReader) {
      try {
        if (typeof codeReader.stopContinuousDecode === 'function') {
          codeReader.stopContinuousDecode();
        }
        if (typeof codeReader.stopAsyncDecode === 'function') {
          codeReader.stopAsyncDecode();
        }
        codeReader.stream = null;
        codeReader.videoElement = null;
      } catch (e) { /* silent */ }
    }
    codeReader = null;
    scanning = false;
    _rotationRun++;   // ends a running rotation supplement loop

    // Now stop our own stream and clear the video element
    if (_activeStream) {
      _activeStream.getTracks().forEach(function (t) { t.stop(); });
      _activeStream = null;
    }
    if (ui.video) ui.video.srcObject = null;
  }

  async function switchCamera() {
    dbg('switchCamera: _cameras=' + _cameras.length + ' idx=' + _cameraIndex + ' scanning=' + scanning);
    if (_cameras.length < 2) {
      dbg('switchCamera: ABBRUCH – weniger als 2 Kameras bekannt');
      // Permission is already granted at this point, so pass postGrant=true
      // to ensure the switch button is shown if multiple cameras are found.
      await enumerateCameras(true);
      dbg('switchCamera: nach Re-Enum: _cameras=' + _cameras.length);
      if (_cameras.length < 2) return;
    }
    _cameraIndex = (_cameraIndex + 1) % _cameras.length;
    var targetDeviceId = _cameras[_cameraIndex] ? _cameras[_cameraIndex].deviceId : null;
    dbg('switchCamera: wechsle zu idx=' + _cameraIndex + ' deviceId=' + (targetDeviceId||'').slice(0,10));

    setStatus('Kamera wird gewechselt…', 'scanning');
    stopActiveStream();
    await startScanning(targetDeviceId);
  }

  /* ============================================================
     Rotation supplement (ZXing path)
     ZXing liest 1D-Barcodes nur entlang waagrechter Pixelzeilen: ein EAN
     wird nur gefunden, solange er höchstens ~20–30° schief liegt (bei
     gekürzten Barcodes weniger). Seitlich oder schräg gehaltene Produkte
     wurden deshalb nie erkannt. Das Kamerabild selbst ist richtig
     ausgerichtet – gedreht ist der Barcode auf der Verpackung.
     Zusätzlich zum 0°-Hauptloop dekodieren wir daher gedrehte Kopien des
     aktuellen Bildes: pro Schritt EIN Winkel, verkleinert, mit einem
     Reader, der nur Handels-Barcodes kennt (EAN/UPC/Code 128). Alle 15°
     decken den ganzen Kreis ab; 180°-Drehungen liest ZXing selbst (jede
     Zeile wird auch rückwärts geprüft). Gedrehte Bilder werden leichter
     falsch gelesen, deshalb zählt ein Treffer erst, wenn derselbe Code
     zweimal gelesen wurde (nächstes Bild, gleicher Winkel).
     Anders als die im März 2026 entfernte Schleife (volle Auflösung,
     TRY_HARDER, jeder rAF) kostet ein Schritt nur wenige ms und läuft
     höchstens alle ROTATION_STEP_MS – der Main-Thread bleibt frei.
     ============================================================ */
  var _rotCanvas   = null;
  var _rotCtx      = null;
  var _rotationRun = 0;              // bumped on stop → old loop ends

  function getRotatedCanvas(video, angleDeg) {
    var vw = video.videoWidth;
    var vh = video.videoHeight;
    if (!vw || !vh) return null;

    // Bounding box of the rotated frame, so nothing is cut off
    var rad = (angleDeg * Math.PI) / 180;
    var cos = Math.abs(Math.cos(rad));
    var sin = Math.abs(Math.sin(rad));
    var bw = vw * cos + vh * sin;
    var bh = vw * sin + vh * cos;
    var scale = Math.min(1, ROTATION_MAX_SIDE / Math.max(bw, bh));
    var cw = Math.round(bw * scale);
    var ch = Math.round(bh * scale);

    if (!_rotCanvas) {
      _rotCanvas = document.createElement('canvas');
      _rotCanvas.setAttribute('data-cookieconsent', 'ignore');
      _rotCtx = _rotCanvas.getContext('2d', { willReadFrequently: true });
    }
    if (_rotCanvas.width !== cw || _rotCanvas.height !== ch) {
      _rotCanvas.width  = cw;
      _rotCanvas.height = ch;
    }

    var ctx = _rotCtx;
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, cw, ch);
    ctx.translate(cw / 2, ch / 2);
    ctx.rotate(rad);
    ctx.scale(scale, scale);
    ctx.drawImage(video, -vw / 2, -vh / 2, vw, vh);
    ctx.restore();
    return _rotCanvas;
  }

  function createRotationDecoder(Z) {
    if (!Z || !Z.MultiFormatReader || !Z.HTMLCanvasElementLuminanceSource ||
        !Z.HybridBinarizer || !Z.BinaryBitmap || !Z.DecodeHintType || !Z.BarcodeFormat) {
      return null;
    }
    var F = Z.BarcodeFormat;
    var hints = new Map();
    hints.set(Z.DecodeHintType.POSSIBLE_FORMATS, [F.EAN_13, F.EAN_8, F.UPC_A, F.UPC_E, F.CODE_128]);
    var reader = new Z.MultiFormatReader();
    reader.setHints(hints);
    return function (canvas) {
      try {
        var source = new Z.HTMLCanvasElementLuminanceSource(canvas);
        var result = reader.decodeWithState(new Z.BinaryBitmap(new Z.HybridBinarizer(source)));
        return result ? result.getText() : null;
      } catch (e) {
        return null;   // NotFoundException – no barcode at this angle
      }
    };
  }

  function startRotationSupplement(Z, video) {
    var decode = createRotationDecoder(Z);
    if (!decode) return;
    var run = ++_rotationRun;
    var idx = 0;
    var pendingText = '';
    var pendingTime = 0;
    (function step() {
      if (!scanning || run !== _rotationRun) return;
      var canvas = getRotatedCanvas(video, ROTATION_ANGLES[idx]);
      var text = canvas ? decode(canvas) : null;
      if (text && text.length >= 8) {
        var now = Date.now();
        if (text === pendingText && (now - pendingTime) < ROTATION_CONFIRM_MS) {
          pendingText = '';
          processBarcode(text, true);
        } else {
          pendingText = text;   // stay on this angle to confirm with the next frame
          pendingTime = now;
        }
      } else {
        idx = (idx + 1) % ROTATION_ANGLES.length;
      }
      setTimeout(step, ROTATION_STEP_MS);
    })();
  }

  /* ============================================================
     Load ZXing — fetch() to bypass Cookiebot
     ============================================================ */
  function loadZXingScript() {
    return new Promise(function (resolve, reject) {
      if (window.ZXing) { resolve(window.ZXing); return; }

      var assetUrl = document.getElementById('zxing-asset-url');
      var src = assetUrl ? assetUrl.value : 'https://unpkg.com/@zxing/library@0.21.3/umd/index.min.js';

      fetch(src)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (code) {
          var s = document.createElement('script');
          s.setAttribute('data-cookieconsent', 'ignore');
          s.textContent = code;
          document.head.appendChild(s);
          if (window.ZXing) { resolve(window.ZXing); }
          else { reject(new Error('ZXing not found after eval')); }
        })
        .catch(function (err) {
          // CDN fallback
          if (src.indexOf('unpkg') === -1) {
            fetch('https://unpkg.com/@zxing/library@0.21.3/umd/index.min.js')
              .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
              .then(function (code) {
                var s = document.createElement('script');
                s.setAttribute('data-cookieconsent', 'ignore');
                s.textContent = code;
                document.head.appendChild(s);
                window.ZXing ? resolve(window.ZXing) : reject(new Error('ZXing CDN fallback failed'));
              })
              .catch(reject);
          } else {
            reject(err);
          }
        });
    });
  }

  /* ============================================================
     Start camera scanning (continuous — does NOT stop on hit)
     forcedDeviceId: when called from switchCamera(), pass the exact
     deviceId to open. This bypasses enumerateCameras()-based selection
     and directly targets the desired lens on multi-camera Android devices.
     ============================================================ */
  async function startScanning(forcedDeviceId) {
    if (scanning) return;

    // Enumerate cameras (updates _cameras list; preserves _cameraIndex if deviceId still present)
    await enumerateCameras();

    // Build video constraint:
    // • If a specific deviceId was passed (camera switch), use it directly.
    // • Otherwise use the _cameraIndex entry, or fall back to facingMode:'environment'.
    var videoConstraint;
    if (forcedDeviceId) {
      // Use exact deviceId — this is the only reliable way to target a specific
      // lens on Android Chrome when multiple back cameras have the same facingMode.
      videoConstraint = { deviceId: { exact: forcedDeviceId } };
    } else if (_cameras.length > 0 && _cameras[_cameraIndex] && _cameras[_cameraIndex].deviceId) {
      videoConstraint = { deviceId: { exact: _cameras[_cameraIndex].deviceId } };
    } else {
      videoConstraint = { facingMode: 'environment' };
    }

    try {
      // Open the actual stream ourselves so we control it from the start
      dbg('startScanning: getUserMedia mit constraint=' + JSON.stringify(videoConstraint));
      var stream = await navigator.mediaDevices.getUserMedia({
        video: Object.assign({ width: { ideal: 1280 }, height: { ideal: 720 } }, videoConstraint)
      });
      _activeStream = stream;

      // Re-enumerate after grant so labels are populated; index is preserved.
      // Pass postGrant=true so the switch button visibility is updated now that
      // the browser exposes real deviceIds and all camera entries.
      await enumerateCameras(true);

      // Sync _cameraIndex to the actually opened stream's deviceId so the
      // next switchCamera() call advances from the correct position.
      // We do this AFTER enumerateCameras(true) so the switch button is already
      // set correctly and this loop cannot un-hide it.
      var openedDeviceId = null;
      var tracks = stream.getVideoTracks();
      if (tracks.length > 0 && tracks[0].getSettings) {
        openedDeviceId = (tracks[0].getSettings() || {}).deviceId;
      }
      if (openedDeviceId) {
        for (var ci = 0; ci < _cameras.length; ci++) {
          if (_cameras[ci].deviceId === openedDeviceId) { _cameraIndex = ci; break; }
        }
      }

      ui.video.srcObject = stream;
      ui.video.hidden = false;
      await ui.video.play();

      if (ui.permissionUI) ui.permissionUI.hidden = true;
      setStatus('Lade Scanner…', 'scanning');

      // Try ZXing
      try {
        _ZXingRef = await loadZXingScript();
      } catch (e) {
        _ZXingRef = null;
      }

      if (_ZXingRef && (_ZXingRef.BrowserMultiFormatReader || _ZXingRef.BrowserBarcodeReader)) {
        var _isBMFR = !!_ZXingRef.BrowserMultiFormatReader;
        var Reader  = _isBMFR ? _ZXingRef.BrowserMultiFormatReader : _ZXingRef.BrowserBarcodeReader;

        // No TRY_HARDER hint — it forces ZXing to scan 8× more rows AND rotate
        // internally on every frame, which blocks the main thread for 300-1000 ms
        // per decode attempt and causes continuous Violation warnings.
        // Standard mode is fast enough for well-lit EAN-13 barcodes in a scanner UI.
        codeReader = _isBMFR ? new Reader() : new Reader(500);
        scanning = true;
        setStatus('Barcode vor die Kamera halten…', 'scanning');

        // decodeContinuously on the already-playing video element.
        // We MUST NOT use decodeFromVideoElement / decodeFromStream because those
        // call reset() internally, which invokes stopStreams() and kills our externally
        // managed MediaStream — causing a black screen and no decoding (the bug observed
        // in incognito mode where getUserMedia is stricter).
        codeReader.videoElement = ui.video;
        codeReader.stream = stream;
        codeReader.decodeContinuously(ui.video, function (result, err) {
          if (result) {
            var text = result.getText();
            if (text && text.length >= 8) processBarcode(text, true);
          }
          // NotFoundException when no barcode in frame — ignore silently
        });

        // Sideways / diagonal barcodes: lightweight rotated decodes next to
        // the 0° loop (see "Rotation supplement" above).
        startRotationSupplement(_ZXingRef, ui.video);

        return;
      }

      // Native BarcodeDetector fallback (stream already open)
      if ('BarcodeDetector' in window) {
        await startNativeScanning(stream);
        return;
      }

      throw new Error('Keine Scan-Bibliothek verfügbar');

    } catch (err) {
      dbg('startScanning FEHLER: ' + err.name + ' – ' + err.message);
      // Clean up stream if something went wrong after opening it
      if (_activeStream) {
        _activeStream.getTracks().forEach(function (t) { t.stop(); });
        _activeStream = null;
      }
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setStatus('Kamerazugriff verweigert. Bitte in den Browser-Einstellungen erlauben.', 'error');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setStatus('Keine Kamera gefunden. Bitte EAN manuell eingeben.', 'error');
      } else {
        setStatus('Scanner konnte nicht gestartet werden. Bitte EAN manuell eingeben.', 'error');
      }
      ui.manualForm.classList.add('active');
      if (ui.permissionUI) ui.permissionUI.hidden = true;
    }
  }

  /* ============================================================
     Native BarcodeDetector fallback
     Stream is already open and assigned to ui.video by startScanning().
     ============================================================ */
  async function startNativeScanning(stream) {
    var detector = new BarcodeDetector({ formats: ['ean_13', 'ean_8', 'upc_a'] });
    // stream + video already set up by caller
    scanning = true;
    setStatus('Barcode vor die Kamera halten…', 'scanning');

    // BarcodeDetector.detect() is async and non-blocking — the browser processes
    // frames on its own thread. Manual rotation is not needed: the platform
    // detectors (ML Kit / Vision) find barcodes in any orientation.
    (function scanFrame() {
      if (!scanning) return;
      detector.detect(ui.video)
        .then(function (results) {
          if (results.length > 0) {
            var ean = results[0].rawValue;
            if (ean && ean.length >= 8) processBarcode(ean, true);
          }
        })
        .catch(function () { /* ignore */ })
        .finally(function () {
          if (scanning) requestAnimationFrame(scanFrame);
        });
    })();
  }
        
          // Initialize on DOMContentLoaded
          document.addEventListener('DOMContentLoaded', function () {
            cacheDom();
            // Scanner UI only exists for logged-in customers
            if (!ui.video || !ui.qtyInput || !ui.manualForm) return;
            bindQuantity();
            bindManualInput();
            if (ui.addBtn) {
              ui.addBtn.addEventListener('click', function () {
                addToCart();
              });
            }
            if (ui.switchCameraBtn) {
              ui.switchCameraBtn.addEventListener('click', function () {
                switchCamera();
              });
            }
            loadRemainingBarcodes();
            if (ui.permissionBtn) {
              ui.permissionBtn.addEventListener('click', function () {
                startScanning();
              });
            }
            // Auto-start scanning if permission already granted
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
              navigator.permissions && navigator.permissions.query({ name: 'camera' }).then(function (result) {
                if (result.state === 'granted') startScanning();
              }).catch(function () {
                // Fallback: try to start scanning anyway
                startScanning();
              });
            }
          });
        
        })();
 